import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "@/components/Layout";
import { api } from "@/lib/api";
import { X } from "lucide-react";

const CATS = [
    { key: "all", label: "All" },
    { key: "environment", label: "Environment" },
    { key: "education", label: "Education" },
    { key: "food", label: "Food" },
    { key: "social", label: "Social" },
    { key: "events", label: "Events" },
];

export default function Gallery() {
    const [cat, setCat] = useState("all");
    const [lightbox, setLightbox] = useState(null);
    const q = useQuery({ queryKey: ["gallery", cat], queryFn: () => api.get(`/gallery${cat !== "all" ? `?category=${cat}` : ""}`).then((r) => r.data) });
    const items = q.data || [];

    return (
        <div data-testid="gallery-page">
            <PageHeader
                eyebrow="Photo Gallery"
                title={<>Moments from <span className="text-secondary italic font-light">the field.</span></>}
                sub="Browse photos from our drives, ceremonies, classrooms and community kitchens."
            />

            <section className="mx-auto max-w-7xl px-5 lg:px-10 py-12">
                <div className="flex flex-wrap gap-2 mb-8">
                    {CATS.map((c) => (
                        <button
                            key={c.key}
                            data-testid={`gallery-filter-${c.key}`}
                            onClick={() => setCat(c.key)}
                            className={`px-5 py-2.5 rounded-full text-sm border transition-all ${
                                cat === c.key
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "bg-background border-border hover:border-primary/40"
                            }`}
                        >
                            {c.label}
                        </button>
                    ))}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {items.map((g, i) => (
                        <button
                            key={g.id}
                            onClick={() => setLightbox(g)}
                            data-testid={`gallery-item-${g.id}`}
                            className={`group overflow-hidden bg-muted ${i % 7 === 0 ? "row-span-2" : ""}`}
                        >
                            <img
                                src={g.image_url}
                                alt={g.title}
                                loading="lazy"
                                className={`w-full object-cover group-hover:scale-105 transition-transform duration-700 ${i % 7 === 0 ? "aspect-[1/2]" : "aspect-square"}`}
                            />
                        </button>
                    ))}
                </div>

                {!q.isLoading && items.length === 0 && (
                    <div className="text-muted-foreground mt-6">No images in this category yet.</div>
                )}
            </section>

            {lightbox && (
                <div
                    data-testid="gallery-lightbox"
                    className="fixed inset-0 z-50 bg-black/90 backdrop-blur grid place-items-center p-4"
                    onClick={() => setLightbox(null)}
                >
                    <button
                        onClick={() => setLightbox(null)}
                        data-testid="lightbox-close"
                        className="absolute top-5 right-5 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center text-white"
                    >
                        <X className="h-5 w-5" />
                    </button>
                    <div className="max-w-5xl w-full">
                        <img src={lightbox.image_url} alt={lightbox.title} className="w-full max-h-[85vh] object-contain" />
                        <p className="text-white/80 text-center mt-3 font-display text-lg">{lightbox.title}</p>
                    </div>
                </div>
            )}
        </div>
    );
}
